import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Check, Crown, CalendarClock, RefreshCcw, Phone } from "lucide-react";
import { Seo } from "@/components/Seo";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { CallButton, WhatsAppButton } from "@/components/CTAs";
import { MEMBERSHIPS, MEMBERSHIP_IMAGE, CONTACT } from "@/data/site";

const PERKS = [
  { icon: RefreshCcw, title: "Session Rollover", text: "Miss a week? Unused sessions roll into the next month on Gold and Platinum." },
  { icon: Phone, title: "Priority Booking", text: "Members skip the queue — one call locks your preferred slot, weekends included." },
  { icon: Crown, title: "Complimentary Upgrades", text: "Jacuzzi upgrades and aroma blending courtesy on higher tiers." },
  { icon: CalendarClock, title: "Flexible Pausing", text: "Travelling? Pause your pass for up to a month, no questions asked." },
];

export default function Membership() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);

  return (
    <main data-testid="membership-page" className="pb-16 md:pb-0">
      <Seo
        title="Spa Membership Plans | Belong Saloon & Spa, Hyderabad"
        description="Monthly massage memberships in Suchitra, Hyderabad — Silver, Gold and Royal Platinum passes with priority booking, rollovers and Jacuzzi upgrades."
      />

      <section ref={ref} className="relative flex min-h-[70vh] items-end overflow-hidden">
        <motion.div style={{ y }} className="absolute inset-0">
          <img src={MEMBERSHIP_IMAGE} alt="Gold-toned luxury spa ambience for Belong Spa members" className="h-[120%] w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-obsidian/75 via-obsidian/50 to-obsidian" />
        </motion.div>
        <div className="relative z-10 mx-auto w-full max-w-7xl px-5 pb-20 pt-44 sm:px-8">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}>
            <Eyebrow className="mb-4">Membership</Eyebrow>
            <h1 className="max-w-3xl font-serif-display text-4xl leading-tight text-cream sm:text-6xl">
              Wellness that <span className="italic text-gold-gradient">belongs</span> to your calendar
            </h1>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-stone-300">
              One massage fixes a week. A routine fixes a life. Our passes make rest a standing appointment.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-32">
        <div className="grid gap-6 lg:grid-cols-3">
          {MEMBERSHIPS.map((m, i) => (
            <Reveal key={m.id} delay={i * 0.1}>
              <div
                data-testid={`membership-card-${m.id}`}
                className={`gold-ring gold-ring-hover relative flex h-full flex-col rounded-3xl p-9 ${
                  m.featured ? "bg-gradient-to-b from-gold/15 via-coal to-coal lg:-translate-y-3" : "bg-coal"
                }`}
              >
                {m.featured && (
                  <span className="absolute -top-3.5 left-1/2 -translate-x-1/2 rounded-full bg-gold px-4 py-1.5 text-[10px] font-bold uppercase tracking-[0.2em] text-stone-950">
                    Most loved
                  </span>
                )}
                <p className="text-xs uppercase tracking-[0.25em] text-gold/80">{m.tagline}</p>
                <h2 className="mt-3 font-serif-display text-3xl text-cream">{m.name}</h2>
                <p className="mt-4 font-serif-display text-xl italic text-gold/80">Monthly wellness pass</p>
                <ul className="mt-8 flex-1 space-y-3.5">
                  {m.features.map((f) => (
                    <li key={f} className="flex items-start gap-3 text-sm text-stone-300">
                      <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-gold/15">
                        <Check size={12} className="text-gold" />
                      </span>
                      {f}
                    </li>
                  ))}
                </ul>
                <a
                  href={CONTACT.phoneTel}
                  data-testid={`membership-join-${m.id}`}
                  className={`mt-9 inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-sm font-semibold transition-all duration-300 active:scale-[0.98] ${
                    m.featured
                      ? "bg-gold text-stone-950 hover:bg-gold-bright"
                      : "border border-gold/40 text-gold hover:bg-gold/10"
                  }`}
                >
                  Join {m.name}
                </a>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.2}>
          <p className="mt-8 text-center text-xs text-stone-500">
            Call {CONTACT.phoneDisplay} for current plans and joining offers.
          </p>
        </Reveal>
      </section>

      <section className="border-y border-gold/10 bg-ember/50 py-24 sm:py-32">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal className="mb-14 max-w-2xl">
            <Eyebrow className="mb-4">Member Privileges</Eyebrow>
            <h2 className="font-serif-display text-3xl text-cream sm:text-4xl">More than a discount</h2>
          </Reveal>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {PERKS.map((p, i) => (
              <Reveal key={p.title} delay={i * 0.08}>
                <div data-testid={`perk-${i}`} className="gold-ring gold-ring-hover h-full rounded-2xl bg-coal p-7">
                  <p.icon size={26} className="text-gold" />
                  <h3 className="mt-5 font-serif-display text-xl text-cream">{p.title}</h3>
                  <p className="mt-2.5 text-sm leading-relaxed text-stone-400">{p.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 py-24 text-center sm:px-8 sm:py-32">
        <Reveal>
          <h2 className="mx-auto max-w-2xl font-serif-display text-3xl leading-tight text-cream sm:text-5xl">
            One call, and next month <span className="italic text-gold-gradient">feels different</span>
          </h2>
          <p className="mx-auto mt-5 max-w-xl text-base text-stone-400">
            Memberships are activated in person or over the phone. Tell us your rhythm — we will suggest the pass that fits it.
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <CallButton testid="membership-call-button" />
            <WhatsAppButton testid="membership-whatsapp-button" />
          </div>
        </Reveal>
      </section>
    </main>
  );
}
